import React from 'react';
import { Container, Stack, Title, Text, Card, Grid, Group, Button, Badge, Image } from '@mantine/core';
import { IconHeart, IconMapPin, IconCalendar, IconExternalLink } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';

const Favorites = () => {
  const { user } = useAuth();

  // Mock data for favorites
  const favorites = [
    {
      id: 1,
      title: "Mountain View Cabin",
      location: "Aspen, Colorado",
      type: "Real Estate",
      price: "$450,000",
      bedrooms: 3,
      bathrooms: 2,
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400",
      addedDate: "2024-01-15"
    },
    {
      id: 2,
      title: "Wilderness Retreat Rental",
      location: "Yellowstone, Montana",
      type: "Rental",
      price: "$200/night",
      bedrooms: 4,
      bathrooms: 3,
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400",
      addedDate: "2024-01-10"
    },
    {
      id: 3,
      title: "RV Park & Campground",
      location: "Grand Canyon, Arizona",
      type: "RV / Camping",
      price: "$35/night",
      amenities: ["Full Hookups", "WiFi", "Pool"],
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400",
      addedDate: "2024-01-08"
    }
  ];

  if (!user) {
    return (
      <Container size="md">
        <Title order={1} ta="center" c="green">Please log in to view your favorites</Title>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Title order={1} c="green">My Favorites</Title>
          <Badge size="lg" color="green" variant="light">
            {favorites.length} Properties
          </Badge>
        </Group>
        
        {favorites.length === 0 ? (
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            <Stack align="center" gap="md">
              <IconHeart size={64} color="var(--mantine-color-gray-4)" />
              <Title order={3} c="dimmed">No favorites yet</Title>
              <Text c="dimmed" ta="center">
                Start exploring properties and add them to your favorites to see them here.
              </Text>
            </Stack>
          </Card>
        ) : (
          <Grid>
            {favorites.map((property) => (
              <Grid.Col key={property.id} span={{ base: 12, sm: 6, md: 4 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
                  <Card.Section>
                    <Image
                      src={property.image}
                      height={200}
                      alt={property.title}
                    />
                  </Card.Section>
                  
                  <Stack gap="sm" mt="md">
                    <Group justify="space-between">
                      <Badge 
                        color={
                          property.type === 'Real Estate' ? 'green' : 
                          property.type === 'Rental' ? 'blue' : 'orange'
                        }
                        variant="light"
                      >
                        {property.type}
                      </Badge>
                      <Button
                        variant="subtle"
                        size="xs"
                        color="red"
                        leftSection={<IconHeart size={14} />}
                      >
                        Remove
                      </Button>
                    </Group>
                    
                    <Title order={4}>{property.title}</Title>
                    
                    <Group gap="xs" c="dimmed">
                      <IconMapPin size={16} />
                      <Text size="sm">{property.location}</Text>
                    </Group>
                    
                    <Group gap="xs" c="dimmed">
                      <IconCalendar size={16} />
                      <Text size="sm">Added {new Date(property.addedDate).toLocaleDateString()}</Text>
                    </Group>
                    
                    <Text size="xl" fw={700} c="green">
                      {property.price}
                    </Text>
                    
                    {property.bedrooms && (
                      <Text size="sm" c="dimmed">
                        {property.bedrooms} bed • {property.bathrooms} bath
                      </Text>
                    )}
                    
                    {property.amenities && (
                      <Group gap="xs">
                        {property.amenities.map((amenity, index) => (
                          <Badge key={index} size="xs" variant="outline">
                            {amenity}
                          </Badge>
                        ))}
                      </Group>
                    )}
                    
                    <Button
                      fullWidth
                      color="green"
                      rightSection={<IconExternalLink size={16} />}
                    >
                      View Details
                    </Button>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

export default Favorites;
